/**
 * Clipboard helpers for the LPO email generator.
 *
 * copyRichText writes BOTH text/plain and text/html so pasting into Outlook
 * keeps the layout, falling back to plain text where ClipboardItem is
 * unavailable (Firefox without the flag, some embedded browsers). It throws
 * the sentinel 'clipboard-unavailable' when nothing could be written, so the
 * caller can surface the text for manual copying; the ledger row has already
 * been recorded by then, which is the intended order (a generation that the
 * user then pastes by hand is still a generation).
 */

export type CopyMode = 'rich' | 'plain';

export async function copyRichText(text: string, html: string): Promise<CopyMode> {
  const clip = typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
  if (clip && typeof ClipboardItem !== 'undefined' && typeof clip.write === 'function') {
    try {
      await clip.write([
        new ClipboardItem({
          'text/plain': new Blob([text], { type: 'text/plain' }),
          'text/html': new Blob([html], { type: 'text/html' }),
        }),
      ]);
      return 'rich';
    } catch {
      // fall through to plain text
    }
  }
  if (await copyPlainText(text)) return 'plain';
  throw new Error('clipboard-unavailable');
}

export async function copyPlainText(text: string): Promise<boolean> {
  const clip = typeof navigator !== 'undefined' ? navigator.clipboard : undefined;
  if (clip && typeof clip.writeText === 'function') {
    try {
      await clip.writeText(text);
      return true;
    } catch {
      // fall through
    }
  }
  return false;
}
